"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Blurhash } from "react-blurhash";
import { LuLock } from "react-icons/lu";

import { SubscribeButton } from "@/components/app/subscribe-button";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { formatUSDCents } from "@/utils/currency";
import { AccessGroupType, getAccessGroupType } from "@/utils/subscriptions";
import type { PartialAccount } from "@/utils/types";

interface GatedContentDialogProps {
  creator: PartialAccount;
  accessGroupKeyName: string;
  blurHash?: string;
  priceToUnlockUsdCents: number;
  type?: "post" | "message";
  messageId?: string;
  postHash?: string;
  onContentRendered?: () => void;
  children?: ReactNode;
}
export const GatedContentDialog = ({
  creator,
  accessGroupKeyName,
  blurHash,
  priceToUnlockUsdCents,
  type = "post",
  messageId,
  postHash,
  onContentRendered,
  children,
}: GatedContentDialogProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [blurHashDimensions, setBlurHashDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    const height = containerRef.current?.clientHeight;
    const width = containerRef.current?.clientWidth;

    if (height && width) {
      setBlurHashDimensions({ height, width });
    }
    onContentRendered?.();
  }, [onContentRendered]);

  return (
    <TriggerLoggedInModalWindow>
      <div
        // Don't increase it from 160px. The placeholder needs to match the
        // minimum possible height of the decrypted content.
        className="
          relative h-[160px] overflow-hidden rounded-xl border border-border
        "
        ref={containerRef}
      >
        {blurHash && blurHashDimensions ? (
          <div className="absolute z-0">
            <Blurhash
              hash={blurHash}
              height={blurHashDimensions.height}
              width={blurHashDimensions.width}
              className="w-full"
            />
          </div>
        ) : (
          <div
            className="
              pointer-events-none absolute bg-accent p-3 opacity-50 blur-xs
            "
          >
            <p className="mb-6">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam eu
              pulvinar nibh. Nam vitae consequat risus. Curabitur consectetur
              massa sit amet dapibus convallis. Proin pharetra pharetra
              accumsan. Ut eget vestibulum neque. Lorem ipsum dolor sit amet,
              consectetur adipiscing elit.
            </p>
            <p>
              Proin sit amet scelerisque tortor. Donec vulputate leo ut dictum
              pulvinar. Duis tempor, neque non congue accumsan, elit orci
              lacinia ante, at ullamcorper nunc nisi at nulla. Ut eleifend ex ac
              justo tincidunt, ac blandit lectus facilisis. Donec a dapibus
              mauris, non convallis nunc. Suspendisse rhoncus bibendum
              scelerisque. Suspendisse potenti.
            </p>
          </div>
        )}
        <div className="relative flex h-full items-center justify-center">
          <div className="absolute z-0 size-full bg-background opacity-50" />
          <div className="relative z-1 flex w-full flex-col gap-2 text-center">
            <SubscribeButton
              creator={creator}
              renderContext={type}
              oneTimePriceToUnlockUsdCents={priceToUnlockUsdCents}
              subscriptionAccessGroupKeyName={accessGroupKeyName}
              messageId={messageId}
              className="mx-auto w-fit"
              postHash={postHash}
              render={({ isSubscribed }) =>
                getAccessGroupType(accessGroupKeyName) ===
                  AccessGroupType.OneTime &&
                typeof priceToUnlockUsdCents === "number" ? (
                  <>
                    <LuLock className="mr-2" /> Unlock for{" "}
                    {formatUSDCents(priceToUnlockUsdCents)}
                  </>
                ) : (
                  `${isSubscribed ? "Upgrade" : "Subscribe"} to Unlock`
                )
              }
            />
            {getAccessGroupType(accessGroupKeyName) ===
              AccessGroupType.Subscription && (
              <p
                className="
                  mt-2 flex justify-center gap-1 text-sm text-muted-foreground
                "
              >
                <span className="font-mono">
                  {formatUSDCents(priceToUnlockUsdCents)}
                </span>
                {accessGroupKeyName.startsWith("focus-sub") ? "/ mo" : ""}
              </p>
            )}
            {children}
          </div>
        </div>
      </div>
    </TriggerLoggedInModalWindow>
  );
};
